import { useRef, useState } from 'react';
import styles from './ExpressionButton.module.scss';
import { FaCode, FaPaste } from 'react-icons/fa';
import Modal, { ModalBody, ModalFooter, ModalHeader } from '../modal/Modal';
import Button from '../button/Button';
import { Header, Label } from '../texts/Texts';
import { useTranslation } from 'react-i18next';
import ExpressionSystem, { ExpressionParameter, ExpressionResult, FormatType } from './ExpressionSystem';
import ExpressionInput, { ExpressionInputRef } from './ExpressionInput';
import useNoInitialEffect from 'common/hooks/useNoInitialEffect';
import { BODY_TOOLTIP } from 'Config';
import { IoCopyOutline } from 'react-icons/io5';
import Storage from 'common/services/Storage';

const EXPRESSION_STORAGE_KEY = 'expression';

export interface ExpressionButtonProps {
    value: string
    absolute?: boolean;
    type?: FormatType;
    disabled?: boolean;
    decimalScale?: number;
    title: string;
    loadParameters: () => ExpressionParameter[];
    onChange: (expression: string | null, value: number | null | undefined) => void;
}

const ExpressionButton = ({
    value,
    absolute,
    decimalScale,
    type,
    title,
    disabled,
    loadParameters,
    onChange,
}: ExpressionButtonProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const { t } = useTranslation();
    const [expression, setExpression] = useState(value);
    const [parameters, setParameters] = useState<ExpressionParameter[]>([]);
    const [result, setResult] = useState<ExpressionResult | null>(null);
    const inputRef = useRef<ExpressionInputRef>(null);

    function handleButtonClick() {
        setIsOpen(true);

        // TODO: adicionar um loading
        const parameters = loadParameters();
        setParameters(parameters);

        setExpression(value);
        setResult(ExpressionSystem.evaluate(value, parameters));
    }

    function onCancel() {
        setIsOpen(false);
    }

    function onSave() {
        onChange(expression, result ? result.value : null);
        setIsOpen(false);
    }

    function handleChange(expr: string | null) {
        const newExpression = expr ?? '';

        setExpression(newExpression);
        setResult(ExpressionSystem.evaluate(newExpression, parameters));
    }

    function copyExpression() {
        Storage.set(EXPRESSION_STORAGE_KEY, expression);
    }

    function pasteExpression() {
        const pasteExpression = Storage.get(EXPRESSION_STORAGE_KEY);
        if (pasteExpression) {
            inputRef.current?.updateValue(pasteExpression);
            setExpression(pasteExpression);
        }
    }

    useNoInitialEffect(() => {
        if (expression !== value) {
            setExpression(value);
        }
    }, [value, decimalScale]);

    return (
        <div className={`${styles.container} ${absolute ? styles.absolute : ''}`}>
            <span className={`${styles.button} ${result?.error ? styles.buttonHasError : ''} ${expression ? styles.buttonHasExpression : ''} ${isOpen ? styles.buttonIsOpen : ''}`} onClick={handleButtonClick}>
                <FaCode />
            </span>

            {isOpen && <Modal style={{ content: { minWidth: 400 } }} isOpen={true}>
                <div>
                    <ModalHeader>
                        <Header>
                            {title}
                        </Header>
                    </ModalHeader>
                    <ModalBody>
                        <div>
                            <div className={styles.resultContainer}>
                                <Label className={styles.resultLabel}>{t('common.result')}:</Label>
                                {(result?.error ? 'ERROR' : ExpressionSystem.formatNumber(ExpressionSystem.numberToStr(result?.value), decimalScale, type))}
                            </div>
                            <ExpressionInput
                                ref={inputRef}
                                value={expression}
                                parameters={parameters}
                                onChange={handleChange}
                                disabled={disabled}
                            />
                        </div>
                    </ModalBody>
                    <ModalFooter className={styles.footer}>
                        <div className={styles.footerLeft}>
                            <Button
                                fw
                                variant='third'
                                onClick={copyExpression}
                                data-tooltip-id={BODY_TOOLTIP}
                                data-tooltip-content={t('common.copy')}
                            >
                                <IoCopyOutline />
                            </Button>
                            <Button
                                fw
                                variant='third'
                                onClick={pasteExpression}
                                data-tooltip-id={BODY_TOOLTIP}
                                data-tooltip-content={t('common.paste')}
                            >
                                <FaPaste />
                            </Button>
                        </div>
                        <div>
                            <Button fw variant='secondary' onClick={() => onCancel()}>{t('common.cancel')}</Button>
                            {!disabled && <Button variant="primary" onClick={() => onSave()}>{t('common.save')}</Button>}
                        </div>
                    </ModalFooter>
                </div>
            </Modal>}
        </div>
    );
};

export default ExpressionButton;
